import { Injectable, inject } from '@angular/core';
import { Doctor } from '@nrwl/rx-models';
import {
  FeatureFlagEvaluationContext,
  FeatureFlagEvaluationFlag,
  FeatureFlagEvaluationResponse,
  FeatureFlagsApiService,
} from '@nrwl/rx-service-feature-flags';
import { Observable, defer, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  createLaunchDarklyContextKey,
  FeatureFlagsStore,
  launchDarklyToFeatureFlagsMap,
} from './feature-flags.store';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private featureFlagsApiService = inject(FeatureFlagsApiService);
  private featureFlagsStore = inject(FeatureFlagsStore);

  public syncFromLaunchDarkly(user: Doctor): Observable<FeatureFlagEvaluationResponse> {
    const contextKey = createLaunchDarklyContextKey(user);

    if (contextKey === null) {
      this.featureFlagsStore.resetLaunchDarkly();
      return of<FeatureFlagEvaluationResponse>({ success: false });
    }

    return defer(() => {
      this.featureFlagsStore.beginLaunchDarklySync(contextKey);

      return this.featureFlagsApiService.evaluateFlags({
        context: this.buildEvaluationContext(user),
        flags: this.getLaunchDarklyFlags(),
      });
    }).pipe(
      catchError(() => of<FeatureFlagEvaluationResponse>({ success: false })),
      tap((response) => this.applyLaunchDarklyResponse(response, contextKey)),
    );
  }

  private buildEvaluationContext(user: Doctor): FeatureFlagEvaluationContext {
    return {
      key: String(user.id),
      email: user.email,
      platform: 'web',
      custom: {
        clinicId: user.clinic?.id,
        companyId: user.company,
      },
    };
  }

  private getLaunchDarklyFlags(): FeatureFlagEvaluationFlag[] {
    return Object.keys(launchDarklyToFeatureFlagsMap).map((key) => ({
      key,
      default: false,
    }));
  }

  private applyLaunchDarklyResponse(
    response: FeatureFlagEvaluationResponse,
    contextKey: string,
  ): void {
    if (!response.success || !response.results) {
      this.featureFlagsStore.setFromLaunchDarkly(null, contextKey);
      return;
    }

    this.featureFlagsStore.setFromLaunchDarkly(response.results, contextKey);
  }
}
